import { intl } from "LocaleWrapper";
import { useMutation } from "@apollo/client";
import { Button, Checkbox, FormGroup, Input, Textarea } from "@cofano/cck";
import { CharterPdfType, ContactRoleType, ShipNominationStatus } from "__generated__/enums";
import { ShipLink } from "utils/LinkUtils";
import "../../styling/styling.scss";
import { PdfButton } from "view/charters/dossier/ship/ShipDetailsBlock";
import ApplicationDefinedStatusChanger, {
	ApplicationDefinedDialogProps,
} from "view/statuses/components/changer/ApplicationDefinedStatusChanger";
import {
	ChangeShipNominationStatusDocument,
	SendCharterPdfDocument,
} from "__generated__/view/charters/graphql/charter.mutations.generated";
import { ShipNominationStatusChangeSalesCharterFragment } from "__generated__/view/charters/graphql/charter.fragments.generated";
import ShipNominationStatusSelect from "view/charters/components/selects/ShipNominationStatusSelect";
import { Controller, useForm } from "react-hook-form";
import {
	NominationStatusChangerInput,
	NominationStatusChangerOutput,
	NominationStatusChangerSchema,
} from "view/charters/utils/charter.schema";
import FormField from "components/cck/FormField";
import { zodResolver } from "@hookform/resolvers/zod";
import SalesCharterEmailRecipientAlert from "view/charters/components/status/SalesCharterEmailRecipientAlert";
import { CustomDialog } from "components/dialogs/Dialogs";
import { ShipNominationStatusChangesDocument } from "__generated__/view/charters/graphql/charter.queries.generated";
import client from "../../../../apollo";

type NominationStatusChangerProps = {
	charter: ShipNominationStatusChangeSalesCharterFragment;
	refetch: () => void;
}
const NominationStatusChanger = ({
	charter,
	refetch,
}: NominationStatusChangerProps) => (
	<ApplicationDefinedStatusChanger
		currentStatus={charter.lastShipNominationStatus?.status}
		initStatusChanges={async () => {
			const result = await client.query({
				query: ShipNominationStatusChangesDocument,
				variables: {
					salesCharterId: charter.id,
				},
			});

			return result.data.shipNominationStatusChanges;
		}}
		renderDialog={(dialogProps) => (
			<NominationStatusChangerDialog charter={charter} refetch={refetch} dialogProps={dialogProps} />
		)}
		historyTitle={intl.formatMessage({ id: "NOMINATIONS" })}
		renderHistoryItem={(statusChange) => (
			<>
				<FormGroup label={intl.formatMessage({ id: "SHIP" })} overflow="hidden">
					<span>
						<ShipLink ship={statusChange.ship} />
					</span>
				</FormGroup>

				<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
					<span>{statusChange.notes ?? "-"}</span>
				</FormGroup>
			</>
		)}
	/>
);

const NominationStatusChangerDialog = ({
	charter,
	refetch,
	dialogProps,
}: ApplicationDefinedDialogProps<NominationStatusChangerProps>) => {

	const [ changeNominationStatus, { loading: changeStatusLoading } ] = useMutation(ChangeShipNominationStatusDocument);
	const [ sendCharterPdf, { loading: sending } ] = useMutation(SendCharterPdfDocument);

	const loading = changeStatusLoading || sending;

	const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<NominationStatusChangerInput, unknown, NominationStatusChangerOutput>({
		resolver: zodResolver(NominationStatusChangerSchema),
		defaultValues: {
			selectedStatus: null,
			notes: "",
			emailNotes: "",
			includePricing: true,
		},
	});

	const onStatusChange = async (data: NominationStatusChangerOutput, sendEmail: boolean) => {
		await changeNominationStatus({
			variables: {
				input: {
					nominationStatus: data.selectedStatus.name,
					voyageId: charter.voyages![0].id,
					charterId: charter.id,
					notes: data.notes,
				},
			},
		});


		if (sendEmail) {
			await sendCharterPdf({
				variables: {
					charterId: charter.id,
					pdfType: CharterPdfType.NominationToCustomer,
					note: data.emailNotes,
					includePricing: data.includePricing,
					isNomination: true,
				},
			});
		}

		dialogProps.onClose();
		refetch();
	};

	const [ selectedStatus, includePricing ] = watch([ "selectedStatus", "includePricing" ]);

	const isChangeToNomination = selectedStatus?.name === ShipNominationStatus.Nominated;

	return (
		<CustomDialog
			{...dialogProps}
			onClose={() => {
				dialogProps.onClose();
				reset();
			}}
			actions={isChangeToNomination ? (
				<>
					<Button
						onClick={handleSubmit(data => onStatusChange(data, false))}
						disabled={loading}
						loading={loading}
						size="small"
						variant="outlined"
					>
						{intl.formatMessage({ id: "NOMINATE" })}
					</Button>

					<Button
						onClick={handleSubmit(data => onStatusChange(data, true))}
						disabled={loading || charter.contacts?.filter(contact =>
							contact.contactRoleType === ContactRoleType.NominationAndConfirmation).length === 0}
						loading={loading}
						size="small"
					>
						{intl.formatMessage({ id: "NOMINATE_AND_SEND" })}
					</Button>
				</>
			) : (
				<Button
					onClick={handleSubmit(data => onStatusChange(data, false))}
					loading={loading}
					disabled={loading}
					size="small"
				>
					{intl.formatMessage({ id: "SAVE" })}
				</Button>
			)}
		>
			<form>
				<FormGroup label={intl.formatMessage({ id: "STATUS" })} required overflow="hidden">
					<FormField errors={errors} name="selectedStatus">
						<Controller
							name="selectedStatus"
							control={control}
							render={({ field }) => (
								<ShipNominationStatusSelect
									{...field}
									currentStatus={charter.lastShipNominationStatus?.status.name}
								/>
							)}
						/>
					</FormField>
				</FormGroup>

				{isChangeToNomination ? (
					<>
						<SalesCharterEmailRecipientAlert
							contacts={charter.contacts ?? []}
							alertBody={intl.formatMessage({ id: "NOMINATE_SHIP_TO_CUSTOMER_CONTENT" })}
						/>

						<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
							<FormField errors={errors} name="notes">
								<Controller
									name="notes"
									control={control}
									render={({ field }) => (
										<Input {...field} />
									)}
								/>
							</FormField>
						</FormGroup>

						<FormGroup label={intl.formatMessage({ id: "EMAIL_NOTE" })} tooltip={intl.formatMessage({ id: "EMAIL_NOTE_TOOLTIP" })}>
							<FormField errors={errors} name="emailNotes">
								<Controller
									name="emailNotes"
									control={control}
									render={({ field }) => (
										<Textarea {...field} />
									)}
								/>
							</FormField>
						</FormGroup>

						<div style={{ display: "flex", alignItems: "center", flexGrow: 1, justifyContent: "space-between", gap: 8 }}>
							<Controller
								name="includePricing"
								control={control}
								render={({ field }) => (
									<Checkbox
										label={intl.formatMessage({ id: "INCLUDE_PRICING_IN_NOMINATION" })}
										onChange={field.onChange}
										checked={field.value}
									/>
								)}
							/>

							<PdfButton
								charter={charter}
								isNomination={true}
								type={CharterPdfType.NominationToCustomer}
								includePricing={includePricing}
							/>
						</div>
					</>
				) : selectedStatus && (
					<FormGroup label={intl.formatMessage({ id: "NOTE" })} overflow="hidden">
						<FormField errors={errors} name="notes">
							<Controller
								name="notes"
								control={control}
								render={({ field }) => (
									<Input {...field} />
								)}
							/>
						</FormField>
					</FormGroup>
				)}
			</form>
		</CustomDialog>
	);
};

export default NominationStatusChanger;
